import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Music, Send, ThumbsUp, Clock, User, Disc3, QrCode, 
  Loader2, AlertCircle, CheckCircle, Search, X, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';

import { API_URL } from '../utils/api';
import { getVoterId } from '../utils/voterId';
import { useConnectionStatus, CONNECTION_STATES } from '../hooks/useConnectionStatus';
import { ConnectionStatusBadge } from '../components/ConnectionStatusIndicator';

/**
 * Validate a first name for DJ Live dance requests.
 * Rules:
 * - Minimum 2 Unicode letters
 * - Maximum 40 characters
 * - Reject single-character entries
 * - Reject names containing digits
 * - Reject symbols-only entries
 * - Allow legitimate short names (Jo, AJ, Li)
 * - Allow hyphenated names (Mary-Jane)
 * - Allow apostrophes (O'Connor)
 * - Support accented and international names
 */
function validateFirstName(name) {
  if (!name) {
    return { isValid: false, error: "Please enter your first name" };
  }
  
  const trimmed = name.trim();
  
  if (!trimmed) {
    return { isValid: false, error: "Please enter your first name" };
  }
  
  if (trimmed.length > 40) {
    return { isValid: false, error: "Name must be 40 characters or less" };
  }
  
  // Check for digits
  if (/\d/.test(trimmed)) {
    return { isValid: false, error: "Please enter your first name without numbers" };
  }
  
  // Count Unicode letters (supports international characters)
  // Match any letter from any language
  const letters = trimmed.match(/\p{L}/gu) || [];
  
  if (letters.length < 2) {
    return { isValid: false, error: "Please enter at least 2 letters in your name" };
  }
  
  // Check for symbols-only (after removing allowed connectors)
  const allowedConnectors = /[-' ]/g;
  const withoutConnectors = trimmed.replace(allowedConnectors, '');
  
  if (!withoutConnectors) {
    return { isValid: false, error: "Please enter your first name" };
  }
  
  // Ensure at least some letters exist
  const hasLetters = /\p{L}/u.test(trimmed);
  if (!hasLetters) {
    return { isValid: false, error: "Please enter your first name using letters" };
  }
  
  return { isValid: true, error: "" };
}

export default function DJLivePage({ mode = 'club' }) {
  const { clubSlug, djSlug } = useParams();
  const entitySlug = mode === 'dj' ? djSlug : clubSlug;
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [highlightedRequestId, setHighlightedRequestId] = useState(null);
  
  // Ref for data diffing
  const lastSessionDataRef = useRef(null);
  
  // API base URL based on mode
  const apiBase = mode === 'dj' 
    ? `${API_URL}/api/djs/${entitySlug}/dj`
    : `${API_URL}/api/clubs/${entitySlug}/dj`;
  
  // Request form state
  const [danceName, setDanceName] = useState('');
  const [requesterName, setRequesterName] = useState(() => 
    localStorage.getItem('dj_requester_name') || ''
  );
  const [nameError, setNameError] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [selectedDance, setSelectedDance] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [votedRequests, setVotedRequests] = useState(() => {
    const saved = localStorage.getItem(`voted_${entitySlug}`);
    return saved ? JSON.parse(saved) : [];
  });
  
  // Validate name whenever it changes
  const nameValidation = useMemo(() => validateFirstName(requesterName), [requesterName]);
  
  // Handle name change with validation
  const handleNameChange = (e) => {
    const newName = e.target.value;
    setRequesterName(newName);
    
    // Only show error if field has been touched and has a value
    if (nameTouched || newName.length > 0) {
      const { isValid, error } = validateFirstName(newName);
      setNameError(isValid ? '' : error);
    }
  };
  
  // Handle name field blur (mark as touched)
  const handleNameBlur = () => {
    setNameTouched(true);
    const { isValid, error } = validateFirstName(requesterName);
    setNameError(isValid ? '' : error);
  };

  // Fetch function for connection status hook
  const fetchData = useCallback(async () => {
    const response = await fetch(`${apiBase}/live`);
    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }
    const text = await response.text();
    if (!text) {
      throw new Error('Empty response');
    }
    return JSON.parse(text);
  }, [apiBase]);

  // Handle data received from connection status hook
  const handleDataReceived = useCallback((data, isFirstFetch) => {
    // Only update state if data actually changed (prevents re-renders)
    const dataStr = JSON.stringify(data);
    if (dataStr !== lastSessionDataRef.current) {
      lastSessionDataRef.current = dataStr;
      setSessionData(data);
    }
    setError(null);
    setLoading(false);
  }, []);

  // Handle data reconciliation after recovery
  const handleReconcile = useCallback((previousData, newData) => {
    console.log('[DJLivePage] Reconciling data after recovery');
    // Update to latest data
    const dataStr = JSON.stringify(newData);
    lastSessionDataRef.current = dataStr;
    setSessionData(newData);
    setError(null);
    // Show toast to inform user
    toast.success('Connection restored', { duration: 2000 });
  }, []);

  // Use connection status hook for managed polling and recovery
  const {
    connectionStatus,
    lastSuccessfulUpdate,
    isStale,
    isRecovering,
    fetch: refreshSession,
    resetConnection
  } = useConnectionStatus({
    fetchFn: fetchData,
    onDataReceived: handleDataReceived,
    onReconcile: handleReconcile,
    pollInterval: 5000,  // 5 second polling for audience page
    config: {
      maxRetries: 3,              // Show "reconnecting" after 3 failures
      disconnectedThreshold: 6,   // Show "disconnected" after 6 failures
      staleDataThreshold: 20000,  // Data is stale after 20 seconds
    }
  });

  // Search for dances
  useEffect(() => {
    // Skip searching once a dance is selected: avoids a redundant external API call
    // and prevents a late response from re-opening the dropdown after selection/submit.
    if (selectedDance || danceName.trim().length < 2) {
      setSearchResults([]);
      setSearching(false);
      setSearchPerformed(false);
      return;
    }

    let cancelled = false;
    const query = danceName;
    setSearching(true);  // set immediately so the no-results state never flashes during debounce
    const debounce = setTimeout(async () => {
      try {
        const response = await fetch(`${API_URL}/api/dances/search?q=${encodeURIComponent(query)}&limit=15`);
        if (!cancelled && response.ok) {
          const results = await response.json();
          if (!cancelled) {
            setSearchResults(results);
            setSearchPerformed(true);
          }
        }
      } catch (err) {
        if (!cancelled) console.error('Search error:', err);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(debounce);
    };
  }, [danceName, selectedDance]);

  // Submit request
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Block submission when disconnected
    if (connectionStatus === CONNECTION_STATES.DISCONNECTED) {
      toast.error('Cannot submit request while offline. Please wait for connection to restore.');
      return;
    }
    
    // Validate name first
    const { isValid, error } = validateFirstName(requesterName);
    if (!isValid) {
      setNameTouched(true);
      setNameError(error);
      toast.error(error);
      return;
    }
    
    if (!danceName.trim()) {
      toast.error('Please select or enter a dance');
      return;
    }

    setSubmitting(true);
    try {
      const firstSong = selectedDance?.songs?.[0] || null;
      const response = await fetch(`${apiBase}/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Voter-ID': getVoterId()
        },
        body: JSON.stringify({
          dance_name: selectedDance?.name || danceName,
          dance_url: selectedDance?.url || null,
          requester_name: requesterName.trim(),
          level: selectedDance?.level || null,
          count: selectedDance?.count || null,
          wall: selectedDance?.wall || null,
          choreographer: selectedDance?.choreographer || null,
          external_dance_id: selectedDance?.dance_id || null,
          external_source: selectedDance?.source || null,
          song_title: firstSong?.title || null,
          song_artist: firstSong?.artist || null,
          music_id: firstSong?.music_id || null,
          stepsheet_url: selectedDance?.stepsheet_url || null,
          songs: selectedDance?.songs || null
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to submit request');
      }

      const result = await response.json();
      localStorage.setItem('dj_requester_name', requesterName);
      setDanceName('');
      setSelectedDance(null);
      setSearchResults([]);
      if (result?.duplicate) {
        toast.error('This dance has already been requested. Please vote for it instead.');
        if (result.existing_request_id) {
          setHighlightedRequestId(result.existing_request_id);
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="request-row-${result.existing_request_id}"]`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 300);
          setTimeout(() => setHighlightedRequestId(null), 5000);
        }
      } else {
        toast.success('Request submitted!');
      }
      refreshSession();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Vote for a request
  const handleVote = async (requestId) => {
    if (votedRequests.includes(requestId)) {
      toast.error('You already voted for this');
      return;
    }

    try {
      // Get persistent voter ID for this browser (DL-5C)
      const voterId = getVoterId();
      
      const response = await fetch(`${apiBase}/request/${requestId}/vote`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Voter-ID': voterId  // Send voter ID in header
        },
        body: JSON.stringify({ voter_id: voterId })  // Also send in body for flexibility
      });

      if (!response.ok) {
        const err = await response.json();
        // Handle "already voted" response gracefully
        if (response.status === 400 && err.detail?.includes('Already voted')) {
          // Sync local state with server state
          const newVoted = [...votedRequests, requestId];
          setVotedRequests(newVoted);
          localStorage.setItem(`voted_${entitySlug}`, JSON.stringify(newVoted));
          toast.error('Already voted for this request');
          return;
        }
        throw new Error(err.detail || 'Failed to vote');
      }

      const newVoted = [...votedRequests, requestId];
      setVotedRequests(newVoted);
      localStorage.setItem(`voted_${entitySlug}`, JSON.stringify(newVoted));
      toast.success('Vote recorded!');
      refreshSession();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const selectDance = (dance) => {
    setSelectedDance(dance);
    setDanceName(dance.name);
    setSearchResults([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-pink-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading session...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-pink-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-orange-600 mb-2 font-medium">Connection lost</p>
          <p className="text-slate-500 text-sm mb-4">Attempting to reconnect...</p>
          <Button onClick={resetConnection} variant="outline" className="border-orange-300 hover:bg-orange-100">
            Retry Now
          </Button>
        </div>
      </div>
    );
  }

  const club = sessionData?.club;
  const currentUrl = window.location.href;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-pink-50">
      {/* Header with Club Info */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-orange-200 sticky top-0 z-40 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              {club?.image_url ? (
                <img 
                  src={club.image_url} 
                  alt={club.name} 
                  className="w-12 h-12 rounded-xl object-cover ring-2 ring-orange-300 shrink-0"
                />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center shadow-lg shrink-0">
                  <Music className="w-6 h-6 text-white" />
                </div>
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <h1 className="text-lg font-bold text-slate-800 truncate">{club?.name}</h1>
                  {/* Connection status badge - shows only when there's an issue */}
                  <ConnectionStatusBadge 
                    status={connectionStatus} 
                    isRecovering={isRecovering}
                  />
                </div>
                {sessionData?.active && (
                  <p className="text-sm text-orange-600 font-medium truncate">{sessionData.session_name}</p>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowQR(true)}
              aria-label="Share this page"
              className="border-orange-400 text-orange-600 hover:bg-orange-100 bg-white shrink-0"
            >
              <QrCode className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Share</span>
            </Button>
          </div>
        </div>
      </div>

      {!sessionData?.active ? (
        /* No Active Session */
        <div className="flex items-center justify-center min-h-[60vh] p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center max-w-md"
          >
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-200 to-pink-200 flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Disc3 className="w-12 h-12 text-orange-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">No Live Session</h2>
            <p className="text-slate-600 mb-6">
              There's no active DJ session at {club?.name} right now. Check back when an event is live!
            </p>
          </motion.div>
        </div>
      ) : (
        /* Active Session Content */
        <div className="container mx-auto px-4 py-6 space-y-6">
          {/* Now Playing */}
          {sessionData.current_dance && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl p-6 shadow-lg"
            >
              <div className="flex items-center gap-2 text-white/90 text-sm mb-3">
                <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
                NOW PLAYING
              </div>
              <h2 className="text-2xl font-bold text-white mb-1">
                {sessionData.current_dance.dance_name}
              </h2>
              {sessionData.current_dance.level && (
                <div className="flex gap-2 mt-2">
                  <Badge className="bg-white/20 text-white border-0">
                    {sessionData.current_dance.level}
                  </Badge>
                  {sessionData.current_dance.count && (
                    <Badge variant="outline" className="border-white/30 text-white">
                      {sessionData.current_dance.count} Count
                    </Badge>
                  )}
                </div>
              )}
              {sessionData.current_dance.dance_url && (
                <a 
                  href={sessionData.current_dance.dance_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-white mt-3"
                >
                  View Stepsheet <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </motion.div>
          )}

          {/* Up Next */}
          {sessionData.upcoming_dances?.length > 0 && (
            <div className="bg-white rounded-2xl p-5 shadow-md border border-orange-100">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Coming Up
              </h3>
              <div className="space-y-3">
                {sessionData.upcoming_dances.map((dance, index) => (
                  <motion.div
                    key={dance.request_id || index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold shadow-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{dance.dance_name}</p>
                      {dance.level && (
                        <p className="text-sm text-slate-500">{dance.level}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* All Requests */}
          {sessionData.requests?.length > 0 && (
            <div className="bg-white rounded-2xl p-5 shadow-md border border-blue-100">
              <h3 className="text-lg font-semibold text-slate-800 mb-1 flex items-center gap-2">
                <ThumbsUp className="w-5 h-5 text-blue-500" />
                Requests ({sessionData.requests.length})
              </h3>
              <p className="text-sm text-slate-500 mb-4" data-testid="voting-explainer">
                Got a favourite? Tap
                <ThumbsUp className="w-3.5 h-3.5 text-pink-500 inline-block align-text-bottom mx-1" />
                to vote and help the DJ see what's popular!
              </p>
              <div className="space-y-3">
                {sessionData.requests.map((request, index) => (
                  <motion.div
                    key={request.request_id}
                    data-testid={`request-row-${request.request_id}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className={`flex items-center gap-4 rounded-xl p-4 transition-all ${
                      highlightedRequestId === request.request_id
                        ? 'ring-2 ring-pink-400 bg-pink-50 border border-pink-300'
                        : request.status === 'queued' 
                        ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-300' 
                        : 'bg-slate-50 border border-slate-100'
                    }`}
                  >
                    <button
                      onClick={() => handleVote(request.request_id)}
                      disabled={votedRequests.includes(request.request_id)}
                      data-testid={`vote-btn-${request.request_id}`}
                      className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors shrink-0 ${
                        votedRequests.includes(request.request_id)
                          ? 'bg-pink-100 text-pink-500'
                          : 'bg-white border border-slate-200 hover:bg-pink-50 text-slate-400 hover:text-pink-500'
                      }`}
                    >
                      <ThumbsUp className={`w-5 h-5 ${votedRequests.includes(request.request_id) ? 'fill-current' : ''}`} />
                      <span className="text-sm font-bold">{request.votes}</span>
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{request.dance_name}</p>
                      {request.choreographer && (
                        <p className="text-sm text-slate-600 truncate" data-testid={`request-choreographer-${request.request_id}`}>
                          {request.choreographer}
                        </p>
                      )}
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
                        <User className="w-3 h-3 shrink-0" />
                        <span className="truncate">Requested by {request.requester_name}</span>
                        {request.level && (
                          <>
                            <span className="shrink-0">•</span>
                            <span className="shrink-0">{request.level}</span>
                          </>
                        )}
                      </div>
                    </div>
                    {request.status === 'queued' && (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-300 shrink-0">Queued</Badge>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Request Form */}
          <div className="bg-white rounded-2xl p-5 shadow-md border border-pink-100">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Send className="w-5 h-5 text-pink-500" />
              Request a Dance
            </h3>
            
            {sessionData.accepting_requests === false ? (
              /* Requests Paused Message */
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-orange-500" />
                </div>
                <h4 className="text-lg font-semibold text-orange-600 mb-2">Requests Paused</h4>
                <p className="text-slate-500 text-sm">
                  The DJ is not accepting new requests at the moment.
                </p>
                {sessionData.scheduled_resume_at && (
                  <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <p className="text-amber-700 text-sm font-medium">
                      Requests will open at{' '}
                      <span className="font-bold">
                        {new Date(sessionData.scheduled_resume_at).toLocaleString('en-GB', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            ) : (
              /* Request Form */
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-600 mb-1 font-medium">What's your first name?</label>
                  <Input
                    value={requesterName}
                    onChange={handleNameChange}
                    onBlur={handleNameBlur}
                    placeholder="e.g. Emma, Jo, Mary-Jane"
                    maxLength={40}
                    className={`bg-slate-50 text-slate-800 focus:ring-pink-400 ${
                      nameError && nameTouched 
                        ? 'border-red-400 focus:border-red-400' 
                        : 'border-slate-200 focus:border-pink-400'
                    }`}
                    data-testid="requester-name-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Please enter your full first name so the DJ knows who requested the dance.
                  </p>
                  {nameError && nameTouched && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1" data-testid="name-error">
                      <AlertCircle className="w-3 h-3" />
                      {nameError}
                    </p>
                  )}
                </div>
                <div className="relative">
                  <label className="block text-sm text-slate-600 mb-1 font-medium">Dance Name</label>
                  <div className="relative">
                    <Input
                      value={danceName}
                      onChange={(e) => {
                        setDanceName(e.target.value);
                        setSelectedDance(null);
                      }}
                      onKeyDown={(e) => {
                        // If Enter is pressed and there's text but no selection, auto-select the typed text
                        if (e.key === 'Enter' && danceName.trim() && !selectedDance) {
                          e.preventDefault();
                          // Auto-select what they typed
                          setSelectedDance({
                            name: danceName.trim(),
                            choreographer: '',
                            level: '',
                            count: '',
                            song: '',
                            source: 'manual'
                          });
                          setSearchResults([]);
                        }
                      }}
                      placeholder="Type a dance name and press Enter..."
                      className="bg-slate-50 border-slate-200 text-slate-800 pr-10 focus:border-pink-400 focus:ring-pink-400"
                      data-testid="dance-name-input"
                    />
                    {searching && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pink-400 animate-spin" />
                    )}
                    {selectedDance && (
                      <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                    )}
                  </div>
                
                {/* Search Results Dropdown */}
                <AnimatePresence>
                  {searchResults.length > 0 && !selectedDance && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden max-h-80 overflow-y-auto"
                    >
                      <div
                        data-testid="search-result-count"
                        className="px-4 py-2 bg-slate-50 border-b border-slate-100 text-xs font-medium text-slate-500 sticky top-0"
                      >
                        {searchResults.length >= 15
                          ? 'Top 15 results shown'
                          : `${searchResults.length} ${searchResults.length === 1 ? 'match' : 'matches'}`}
                      </div>
                      {searchResults.map((dance, index) => (
                        <button
                          key={index}
                          type="button"
                          data-testid={`search-result-${index}`}
                          onClick={() => selectDance(dance)}
                          className="w-full px-4 py-3 text-left hover:bg-pink-50 transition-colors border-b border-slate-100 last:border-0"
                        >
                          <p className="font-medium text-slate-800 truncate">{dance.name}</p>
                          <p className="text-sm text-slate-500 truncate">
                            {dance.choreographer}{dance.choreographer && dance.level ? ' • ' : ''}{dance.level}
                            {dance.song && <span className="text-slate-400"> • {dance.song}</span>}
                          </p>
                          <div className="flex items-center flex-wrap gap-1 mt-1">
                            {dance.matched_on === 'song' && (
                              <span
                                data-testid={`search-result-songmatch-${index}`}
                                className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-600 inline-flex items-center gap-1"
                              >
                                <Music className="w-3 h-3" />Song match
                              </span>
                            )}
                            {dance.source && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                dance.source === 'bootstepper'
                                  ? 'bg-blue-100 text-blue-600'
                                  : dance.source === 'copperknob'
                                  ? 'bg-amber-100 text-amber-600'
                                  : 'bg-slate-100 text-slate-500'
                              }`}>
                                {dance.source === 'bootstepper' ? 'BootStepper' :
                                 dance.source === 'copperknob' ? 'CopperKnob' : 'Local'}
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                      {/* Hint at bottom of dropdown */}
                      <div className="px-4 py-2 bg-slate-50 border-t border-slate-100">
                        {searchResults.length >= 15 && (
                          <p className="text-xs text-slate-500 mb-1" data-testid="search-more-hint">
                            Showing top 15 — keep typing (e.g. the choreographer or song) to narrow it down
                          </p>
                        )}
                        <p className="text-xs text-slate-500">Or press Enter to request "{danceName.trim()}" as typed</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* No-results state (never while loading; manual request still allowed) */}
              {!selectedDance && !searching && searchPerformed && searchResults.length === 0 && danceName.trim().length >= 2 && (
                <div
                  className="p-3 bg-amber-50 border border-amber-200 rounded-lg"
                  data-testid="search-no-results"
                >
                  <p className="text-sm text-slate-700">
                    No matches found for "<span className="font-medium">{danceName.trim()}</span>".
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    You can still request it by typing the dance name.
                  </p>
                  <button
                    type="button"
                    data-testid="request-as-typed-btn"
                    onClick={() => {
                      setSelectedDance({
                        name: danceName.trim(),
                        choreographer: '',
                        level: '',
                        count: '',
                        song: '',
                        source: 'manual'
                      });
                      setSearchResults([]);
                    }}
                    className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-pink-600 hover:text-pink-700"
                  >
                    Request "{danceName.trim()}" as typed
                    <Send className="w-3 h-3" />
                  </button>
                </div>
              )}

              {selectedDance && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="selected-dance-chip">
                  <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-green-700 font-medium truncate">{selectedDance.name}</p>
                    {selectedDance.choreographer && (
                      <p className="text-xs text-green-700 truncate" data-testid="selected-dance-choreographer">{selectedDance.choreographer}</p>
                    )}
                    {(() => {
                      const meta = [
                        selectedDance.level,
                        selectedDance.count ? `${selectedDance.count} Count` : null
                      ].filter(Boolean).join(' • ');
                      return (
                        <>
                          {meta && <p className="text-xs text-green-600 truncate">{meta}</p>}
                          {selectedDance.song
                            ? <p className="text-xs text-green-600 break-words" data-testid="selected-dance-song">♪ {selectedDance.song}</p>
                            : (!meta && selectedDance.source === 'manual'
                                ? <p className="text-xs text-green-600">Will be requested as typed</p>
                                : null)}
                        </>
                      );
                    })()}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedDance(null);
                      setDanceName('');
                    }}
                    className="text-green-500 hover:text-green-600 p-1"
                    aria-label="Clear selected dance"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              <Button 
                type="submit" 
                disabled={submitting || !danceName.trim() || !nameValidation.isValid || connectionStatus === CONNECTION_STATES.DISCONNECTED}
                className="w-full bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-request-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : connectionStatus === CONNECTION_STATES.DISCONNECTED ? (
                  <AlertCircle className="w-4 h-4 mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {connectionStatus === CONNECTION_STATES.DISCONNECTED ? 'Offline' : 'Submit Request'}
              </Button>
              <p className="text-xs text-slate-500 text-center">
                Maximum 3 pending requests per person
              </p>
            </form>
            )}
          </div>

          {/* Stats */}
          {sessionData.played_count > 0 && (
            <div className="text-center text-slate-500 text-sm py-4 font-medium">
              🎵 {sessionData.played_count} dances played this session
            </div>
          )}
        </div>
      )}

      {/* QR Code Modal */}
      <AnimatePresence>
        {showQR && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowQR(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-8 max-w-sm w-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-slate-900 mb-2">Share This Page</h3>
              <p className="text-slate-600 mb-6">Scan to request dances at {club?.name}</p>
              <div className="bg-white p-4 rounded-xl inline-block mb-4">
                <QRCode value={currentUrl} size={200} />
              </div>
              <p className="text-sm text-slate-400 break-all">{currentUrl}</p>
              <Button 
                className="mt-4 w-full"
                onClick={() => {
                  navigator.clipboard.writeText(currentUrl);
                  toast.success('Link copied!');
                }}
              >
                Copy Link
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
